<template>
  <div>
    <OfferTermsCardComponent
      ref="termsCard"
      :registration-authority="selectionAuthority"
      :has-selection
      :price-hint="$t('soldera_inventory.price_hint')"
    />

    <OfferActionsComponent
      :title="$t('soldera_inventory.select_certificates')"
      :can-submit
      :is-submitting
      :blocking-reason
      :show-blocking-reason="hasSelection"
      :sent="sentOffer"
      @submit="sendOffer"
    >
      <template #actions>
        <a
          v-if="sheetLink"
          :href="sheetLink"
          target="_blank"
          rel="noopener noreferrer"
          class="small"
          data-testid="sheet-link"
        >
          {{ $t("soldera_inventory.open_breakdown") }}
        </a>
        <button
          type="button"
          class="btn btn-outline-success"
          data-testid="generate-sheet-btn"
          :disabled="!hasSelection || isGeneratingSheet"
          @click="generateSheet"
        >
          <span
            v-if="isGeneratingSheet"
            class="spinner-border spinner-border-sm me-2"
            role="status"
          ></span>
          {{ $t("soldera_inventory.generate_breakdown") }}
        </button>
      </template>
    </OfferActionsComponent>

    <LoadingIndicatorComponent
      v-if="isLoadingItems"
      :message="$t('common.loading')"
      class="w-100"
      data-testid="inventory-refresh-loading"
    />
    <!-- Keep the panel mounted so refreshing volumes preserves its filters and auto-select settings. -->
    <InventorySelectionPanel
      v-show="!isLoadingItems"
      :items="listedItems"
      :selection
      :loading="isLoadingItems"
      :table-options="TABLE_OPTIONS"
    >
      <template #breakdowns="{ items, getMwh, total }">
        <BreakdownSectionComponent
          :title="$t('soldera_inventory.regions')"
          :entries="regionEntries(items, getMwh)"
          :total
        />
      </template>
    </InventorySelectionPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { SOLDERA_INVENTORY_COLUMNS } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { allItemsSelectable } from "@/dashboard/inventory_management/common/portfolioItemsUtils";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import BreakdownSectionComponent, {
  type BreakdownEntry,
} from "@/inventory_selection/BreakdownSectionComponent.vue";
import { groupMwh } from "@/inventory_selection/groupMwh";
import InventorySelectionPanel from "@/inventory_selection/InventorySelectionPanel.vue";
import type {
  InventoryTableOptions,
  ItemMwh,
} from "@/inventory_selection/inventorySelectionTypes";
import type { FlattenedPortfolioItem } from "@/types";

import { offerBlockingKey, singleAuthority } from "../common/offerGate";
import OfferActionsComponent from "../common/OfferActionsComponent.vue";
import OfferTermsCardComponent from "../common/OfferTermsCardComponent.vue";
import { useLatestListing } from "../common/useLatestListing";
import { useOfferTerms } from "../common/useOfferTerms";
import { solderaInventoryApi } from "../solderaInventoryApi";
import type {
  OfferResponse,
  SpotInventoryItem,
  SpotInventoryResponse,
} from "../solderaInventoryTypes";

const TABLE_OPTIONS: InventoryTableOptions = {
  storageContext: "soldera-inventory",
  defaultVisibleColumns: SOLDERA_INVENTORY_COLUMNS,
  availableColumns: SOLDERA_INVENTORY_COLUMNS,
};

/**
 * Spot tab of Soldera Inventory: certificates Soldera can deliver now, selected in the shared
 * selection panel and bid on with the shared offer terms.
 *
 * The listing lives here, never in the shared portfolio cache: that cache holds the buyer's own
 * certificates, which must not show up as Soldera's.
 */
const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const terms = useOfferTerms();
const listedItems = shallowRef<FlattenedPortfolioItem[]>([]);
const selection = usePortfolioSelection({
  policy: allItemsSelectable,
  items: listedItems,
});
const termsCard =
  useTemplateRef<InstanceType<typeof OfferTermsCardComponent>>("termsCard");

const isSubmitting = ref(false);
const isGeneratingSheet = ref(false);
const sheetLink = ref<string | null>(null);
const sentOffer = ref<OfferResponse | null>(null);

// Minimum-price rows are listed only once the price clears their floor, so the listing follows the
// price; until the refetch lands the rows are those of the old price, so sending waits for it.
const {
  isLoading: isLoadingItems,
  listingFailed,
  refetch: fetchItems,
} = useLatestListing(
  async (): Promise<SpotInventoryResponse> =>
    (await solderaInventoryApi.spot.listItems(terms.listingPrice)).data,
  (data) => {
    listedItems.value = (data?.items ?? []).map(toTableItem);
  },
  {
    sources: [() => terms.listingPrice],
    debounceMs: 500,
    onError: globalErrorHandler,
  }
);

const hasSelection = computed(() => selection.hasSelectedItems);

const selectedAuthorities = computed(
  () =>
    new Set(selection.selectedItems.map((item) => item.registration_authority))
);

// The scheme the counterparty's registry account must belong to; the picker validates against it.
const selectionAuthority = computed(
  () => singleAuthority(selectedAuthorities.value) ?? null
);

const blockingReason = computed((): string | null => {
  const key = offerBlockingKey({
    hasSelection: hasSelection.value,
    noSelectionKey: "soldera_inventory.blocked.no_selection",
    termsBlockingKey: terms.blockingKey,
    authorities: selectedAuthorities.value,
  });
  return key && t(key);
});

const canSubmit = computed(
  () =>
    blockingReason.value === null &&
    !isSubmitting.value &&
    !isLoadingItems.value &&
    !listingFailed.value
);

// A sheet describes one selection; once the selection changes the link would describe another.
// A refetch that leaves the selection as it was keeps the link: a sheet carries no price.
watch(
  () => selection.selectedItemIds,
  () => {
    sheetLink.value = null;
  }
);

function regionEntries(
  items: FlattenedPortfolioItem[],
  getMwh: ItemMwh
): BreakdownEntry[] {
  return [...groupMwh(items, (item) => item.issuing_region, getMwh)]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, mwh]) => ({ label, mwh }));
}

// The shared table and selection read the flattened-item shape; the fields a buyer never receives
// are blanked, and the producer group stands in for the company so selection can spread over it.
// Every listed row is on offer to the buyer, whatever its internal spot type or status.
function toTableItem(item: SpotInventoryItem): FlattenedPortfolioItem {
  return {
    ...item,
    portfolio_type: "SPOT_MARKET_PRICE",
    status: "AVAILABLE",
    company_order_id: null,
    reserved_by_active_order: false,
    portfolio_id: 0,
    portfolio_name: "",
    is_static_type: true,
    is_default: false,
    company_id: item.producer_group,
    company_name: "",
    account_emails: [],
    certificate_id: item.id,
    transfer_deadline_passed: false,
    sales_transaction_item_id: null,
    production_device_name: null,
    device_group_id: null,
    device_group_name: null,
    registry_status: null,
    transferred_at: null,
    limit_price: null,
    sale_price: null,
  };
}

async function generateSheet() {
  // Browsers only allow a new tab during the click itself, not once the request returns, so open
  // it now and point it at the sheet when the link arrives.
  const sheetTab = window.open("", "_blank");
  if (sheetTab) sheetTab.opener = null;
  isGeneratingSheet.value = true;
  try {
    const { data } = await solderaInventoryApi.spot.createSheet({
      portfolio_items: selection.selectedItemsForApi,
    });
    sheetLink.value = data.spreadsheet_link;
    if (sheetTab) sheetTab.location.href = data.spreadsheet_link;
    globalSuccessHandler(
      t("soldera_inventory.breakdown_ready_title"),
      t("soldera_inventory.breakdown_ready_description", {
        email: terms.accountEmail,
      })
    );
  } catch (error: unknown) {
    sheetTab?.close();
    globalErrorHandler(error);
  } finally {
    isGeneratingSheet.value = false;
  }
}

async function sendOffer() {
  if (!canSubmit.value || !termsCard.value?.validate()) return;
  isSubmitting.value = true;
  try {
    const { data } = await solderaInventoryApi.spot.createOffer({
      portfolio_items: selection.selectedItemsForApi,
      ...terms.toRequest(),
    });
    globalSuccessHandler(
      t("soldera_inventory.created_title"),
      t("soldera_inventory.created_description", { mwh: data.volume_mwh })
    );
    sentOffer.value = data;
    selection.clearSelection();
    await fetchItems();
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isSubmitting.value = false;
  }
}
</script>
